import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiErr, fmtDate, availLabel, checkoutOriginUrl, goToCheckout } from "../api";
import { isNative } from "../mobileAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { CalendarClock, AlertTriangle } from "lucide-react";

const TYPE_LABEL = { open_space: "Open Space", cabina: "Cabina Privacy" };
const romeDate = (offsetDays = 0) => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome" }).format(new Date(Date.now() + offsetDays * 86400000));
const todayStr = () => romeDate(0);
const tomorrow = () => romeDate(1);

export function MyBookings({ onChange }) {
  const [items, setItems] = useState([]);
  const [cancelT, setCancelT] = useState(null);
  const [resT, setResT] = useState(null);
  const [newDate, setNewDate] = useState(tomorrow());
  const [av, setAv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [preparing, setPreparing] = useState(false);

  const load = () => {
    api.get("/my/bookings").then((r) => {
      setItems(r.data.filter((b) => b.status === "confirmed" && !b.is_past));
    }).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    if (!resT || !newDate) { setAv(null); return; }
    let stale = false;
    api.get(`/availability?date=${newDate}&station_type=${resT.station_type}`)
      .then((r) => { if (!stale) setAv(r.data); })
      .catch(() => { if (!stale) setAv(null); });
    return () => { stale = true; };
  }, [resT, newDate]);

  // Spostamento tardivo (15€): pre-creiamo la sessione Stripe alla scelta della data.
  // iOS Safari, dentro l'iframe cross-origin del Preview, BLOCCA la navigazione top-level
  // se avviene dopo un await/fetch (gesto utente già consumato). Pre-caricando l'URL, il
  // tap sul link <a target="_top"> è una navigazione NATIVA nel gesto → funziona su iOS.
  // La prenotazione NON viene spostata qui: lo spostamento avviene solo dopo il pagamento.
  useEffect(() => {
    const isLateNow = resT && resT.can_reschedule && !resT.can_reschedule_free;
    const pendNow = resT && resT.pending_reschedule;
    const okDate = resT && newDate && newDate !== resT.date && newDate >= todayStr();
    const avOkNow = av && av.date === newDate && (av.state === "DISPONIBILE" || av.state === "ULTIMA POSTAZIONE");
    const matchPend = pendNow && newDate === pendNow.new_date;
    if (!isLateNow || !okDate || !(avOkNow || matchPend)) { setCheckoutUrl(null); return; }
    let stale = false;
    setPreparing(true); setCheckoutUrl(null);
    api.post(`/bookings/${resT.id}/reschedule/checkout`, { new_date: newDate, origin_url: checkoutOriginUrl(), native: isNative() })
      .then((r) => { if (!stale) setCheckoutUrl(r.data && r.data.checkout_url ? r.data.checkout_url : null); })
      .catch((e) => { if (!stale) { setCheckoutUrl(null); toast.error(apiErr(e)); } })
      .finally(() => { if (!stale) setPreparing(false); });
    return () => { stale = true; };
  }, [resT, newDate, av]);

  const isLate = resT && resT.can_reschedule && !resT.can_reschedule_free; // <72h → 15 €

  const doCancel = async () => {
    if (!cancelT) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${cancelT.id}/cancel`);
      toast.success(data.refunded ? "Prenotazione annullata. Giornata riaccreditata." : "Prenotazione annullata. Giornata non riaccreditata (entro 72h).");
      setCancelT(null); load(); onChange && onChange();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doReschedule = async () => {
    // Percorso GRATUITO (>=72h): nessun redirect esterno, navigazione interna.
    if (!resT) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${resT.id}/reschedule/free`, { new_date: newDate });
      toast.success("Prenotazione spostata. Nessun costo (oltre 72 ore).");
      setResT(null); load(); onChange && onChange();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (items.length === 0) return null;
  const avOk = av && (av.state === "DISPONIBILE" || av.state === "ULTIMA POSTAZIONE");
  const pend = resT && resT.pending_reschedule;
  const matchesPending = pend && newDate === pend.new_date;
  const canConfirm = resT && newDate !== resT.date && newDate >= todayStr() && (avOk || matchesPending);

  const openReschedule = (b) => { setResT(b); setNewDate(b.pending_reschedule?.new_date || tomorrow()); };

  return (
    <div className="card p-5 mb-4" data-testid="my-bookings">
      <div className="flex items-center gap-2 mb-3"><CalendarClock size={18} color="#7C1FD6" /><span className="font-semibold">Le mie prenotazioni</span></div>
      <div className="space-y-3">
        {items.map((b) => {
          const canMove = b.can_reschedule_free || b.can_reschedule;
          const canCancel = b.can_cancel_free || b.can_cancel_late;
          const late = b.can_reschedule && !b.can_reschedule_free;
          return (
            <div key={b.id} className="rounded-xl border border-gray-200 p-3" data-testid={`booking-${b.id}`}>
              <div className="font-medium" data-testid={`booking-date-${b.id}`}>{fmtDate(b.date)} · {TYPE_LABEL[b.station_type]}</div>
              {late && (
                <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>Entro le 72 ore: lo spostamento costa 15 € e l'annullamento non riaccredita la giornata.</span>
                </div>
              )}
              {(canMove || canCancel) ? (
                <div className="flex gap-2 mt-3">
                  {canMove && (
                    <button data-testid={`reschedule-${b.id}`} onClick={() => openReschedule(b)} className="btn-primary text-sm flex-1">
                      {late ? "Sposta data (15 €)" : "Sposta data"}
                    </button>
                  )}
                  {canCancel && (
                    <button data-testid={`cancel-${b.id}`} onClick={() => setCancelT(b)} className="btn-outline text-sm flex-1">Annulla prenotazione</button>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-400 mt-2">Giornata in corso — nessuna azione disponibile.</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Conferma annullamento */}
      <Dialog open={!!cancelT} onOpenChange={(o) => !o && setCancelT(null)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Annulla prenotazione</DialogTitle>
            <DialogDescription>
              {cancelT?.can_cancel_free
                ? "Mancano più di 72 ore: l'annullamento è gratuito e la giornata verrà riaccreditata sul tuo saldo."
                : "Mancano meno di 72 ore: la giornata verrà persa e NON sarà riaccreditata. Vuoi procedere?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <button data-testid="cancel-abort" onClick={() => setCancelT(null)} className="btn-outline flex-1">Indietro</button>
            <button data-testid="cancel-confirm" disabled={busy} onClick={doCancel} className="btn-primary flex-1">{busy ? "Attendere…" : "Conferma annullamento"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Spostamento */}
      <Dialog open={!!resT} onOpenChange={(o) => !o && setResT(null)}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle>{isLate ? "Spostamento tardivo — 15 €" : "Sposta data — gratuito"}</DialogTitle>
            <DialogDescription>
              Sposta la tua prenotazione {resT ? TYPE_LABEL[resT.station_type] : ""} ad un'altra data disponibile. Viene mantenuta la stessa tipologia di postazione.
            </DialogDescription>
          </DialogHeader>
          <label className="block text-sm text-gray-600 mt-1">Nuova data
            <input data-testid="reschedule-date" type="date" value={newDate} min={todayStr()}
              onChange={(e) => setNewDate(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-3 mt-1" />
          </label>
          {resT && newDate === resT.date && (
            <p className="text-sm text-amber-600 mt-1" data-testid="reschedule-samedate">Seleziona una data diversa da quella attuale.</p>
          )}
          {av && (
            <div className="text-sm mt-2" data-testid="reschedule-availability">
              Stato: <b className={av.state === "COMPLETO" ? "text-red-600" : "text-green-700"}>{availLabel(av.state, resT && resT.station_type)}</b>
            </div>
          )}
          {av && av.state === "COMPLETO" && (
            <p className="text-sm text-red-600 mt-1">Questa data non è disponibile (completa o bloccata). Scegline un'altra.</p>
          )}
          {resT && (
            <div className={`rounded-lg p-3 mt-3 text-sm border ${isLate ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-violet-50 border-violet-200 text-violet-900"}`}>
              Da <b>{fmtDate(resT.date)}</b> → <b>{fmtDate(newDate)}</b> · {TYPE_LABEL[resT.station_type]}{isLate ? " · Spostamento tardivo: 15 €" : " · Gratuito"}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2 mt-2">
            <button data-testid="reschedule-abort" onClick={() => setResT(null)} className="btn-outline flex-1">Annulla</button>
            {isLate ? (
              checkoutUrl ? (
                <a data-testid="reschedule-confirm" href={checkoutUrl} target="_top" rel="noopener"
                   onClick={(e) => { if (isNative()) { e.preventDefault(); goToCheckout(checkoutUrl); } }}
                   className="btn-primary flex-1 text-center">Paga 15 € e sposta</a>
              ) : (
                <button data-testid="reschedule-confirm" disabled
                   className="btn-outline flex-1 opacity-50 cursor-not-allowed">
                  {preparing ? "Preparazione pagamento…" : "Seleziona una data disponibile"}
                </button>
              )
            ) : (
              <button data-testid="reschedule-confirm" disabled={!canConfirm || busy} onClick={doReschedule}
                className={`flex-1 ${canConfirm && !busy ? "btn-primary" : "btn-outline opacity-50 cursor-not-allowed"}`}>
                {busy ? "Attendere…" : "Conferma spostamento"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
